import { Activity, Upload, CheckCircle, Clock } from 'lucide-react';

interface ScanProgressProps {
  job: {
    status: string;
    progress_percentage: number;
    scanned_tiles: number;
    total_tiles: number;
    uploaded_files: number;
    total_files: number;
  };
}

export default function ScanProgress({ job }: ScanProgressProps) {
  const getProgressColor = () => {
    if (job.status === 'completed') return 'bg-green-600';
    if (job.status === 'failed') return 'bg-red-600';
    if (job.status === 'uploading') return 'bg-purple-600';
    return 'bg-blue-600';
  };

  const getStatusIcon = () => {
    switch (job.status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'scanning':
        return <Activity className="w-6 h-6 text-blue-600 animate-pulse" />;
      case 'uploading':
        return <Upload className="w-6 h-6 text-purple-600 animate-bounce" />;
      default:
        return <Clock className="w-6 h-6 text-gray-400" />;
    }
  };

  const getStatusMessage = () => {
    switch (job.status) {
      case 'scanning':
        return 'Scanning in progress...';
      case 'uploading':
        return 'Uploading files to server...';
      case 'completed':
        return 'Scan completed successfully!';
      case 'failed':
        return 'Scan failed';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
      <div className="flex items-center gap-4 mb-4">
        {getStatusIcon()}
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            {getStatusMessage()}
          </h3>
          <p className="text-sm text-gray-600">
            {Math.round(job.progress_percentage)}% complete
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">
            {Math.round(job.progress_percentage)}%
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="relative">
        <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
          <div
            className={`${getProgressColor()} h-4 rounded-full transition-all duration-500 relative`}
            style={{ width: `${job.progress_percentage}%` }}
          >
            {/* Animated stripes for active scanning */}
            {['scanning', 'uploading'].includes(job.status) && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-shimmer" />
            )}
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 gap-4 mt-6">
        <div className="bg-white rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Scan Progress</span>
            <Activity className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-xl font-bold text-gray-900">
            {job.scanned_tiles} / {job.total_tiles || '?'}
          </div>
          <div className="text-xs text-gray-500">tiles scanned</div>
        </div>

        <div className="bg-white rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Upload Progress</span>
            <Upload className="w-4 h-4 text-purple-600" />
          </div>
          <div className="text-xl font-bold text-gray-900">
            {job.uploaded_files} / {job.total_files || '?'}
          </div>
          <div className="text-xs text-gray-500">files uploaded</div>
        </div>
      </div>

      {/* Status-specific messages */}
      {job.status === 'scanning' && (
        <div className="mt-4 p-3 bg-blue-100 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            ⏳ Scanner is actively capturing images. Please do not close this window.
          </p>
        </div>
      )}

      {job.status === 'uploading' && (
        <div className="mt-4 p-3 bg-purple-100 border border-purple-200 rounded-lg">
          <p className="text-sm text-purple-800">
            ☁️ Files are being uploaded to the server. This may take a few minutes.
          </p>
        </div>
      )}

      {job.status === 'completed' && (
        <div className="mt-4 p-3 bg-green-100 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            ✅ All files have been successfully uploaded and are ready for viewing.
          </p>
        </div>
      )}
    </div>
  );
}
